import { useInFlightIds } from '@mdvip/shared/hooks/useInFlightIds';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useState } from 'react';
import { closeDocumentTab, preOpenDocumentTab, showDocumentInTab } from './openDocumentTab';
import type { AwpDocument } from './types';

export interface DocumentDownloadError {
  code: string;
  doc: AwpDocument;
}

interface UseDocumentDownloadOptions {
  getDownloadUrl: (doc: AwpDocument) => Promise<string>;
  onDocumentGone?: () => void;
}

// A fresh error, never the caught one: a ClientError's message embeds the request variables (the phrId).
function report(code: string) {
  trackException(new Error(`awpDocumentDownload failed: ${code}`), { source: 'awpDocumentDownload', errorCode: code });
}

/**
 * Opens a document's PDF in a new tab, tracking each row's request separately so several can run at
 * once. `download` must be called straight from the click handler: the tab is opened before the
 * URL is requested, while the browser still counts it as part of the gesture.
 *
 * The URL is a short-lived bearer credential, so it is used once and never stored.
 */
export function useDocumentDownload({ getDownloadUrl, onDocumentGone }: UseDocumentDownloadOptions) {
  const inFlight = useInFlightIds();
  const [error, setError] = useState<DocumentDownloadError | null>(null);

  const download = (doc: AwpDocument) => {
    if (inFlight.ids.has(doc.id)) {
      return;
    }
    const tab = preOpenDocumentTab();
    inFlight.add(doc.id);
    setError(null);
    void run(doc, tab);
  };

  const run = async (doc: AwpDocument, tab: Window | null) => {
    let code: string | null = null;
    try {
      const url = await getDownloadUrl(doc);
      try {
        await showDocumentInTab(tab, url);
      } catch {
        // Not a request failure, so extractGqlErrorCode would misread its TypeError as NETWORK_ERROR.
        code = 'UNKNOWN';
      }
    } catch (cause) {
      code = extractGqlErrorCode(cause);
    } finally {
      inFlight.remove(doc.id);
    }
    if (code) {
      closeDocumentTab(tab);
      report(code);
      setError({ code, doc });
    }
    if (code === 'NOT_FOUND') {
      try {
        onDocumentGone?.();
      } catch {
        // The row's own error already stands; a failing refetch callback mustn't replace it.
        report('DOCUMENT_GONE_CALLBACK_FAILED');
      }
    }
  };

  const retry = () => {
    if (error && error.code !== 'NOT_FOUND') {
      download(error.doc);
    }
  };

  return {
    download,
    pendingIds: inFlight.ids,
    error,
    retry,
    dismissError: () => setError(null),
  };
}
